import { FileText, LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { EventFileModel } from '../../models/Evento';
import { FileMutationInput } from '../../hooks/useEventoDetail';
import FileUploadFormModal from './FileUploadFormModal';
import { EyeButton, PlusButton, TrashButton } from 'shared/components/IconButtons';
import DeleteModal from 'shared/components/DeleteModal';

interface Props {
  title: string;
  itemLabel: string;
  icon: LucideIcon;
  files: EventFileModel[];
  isSaving: boolean;
  onAdd: (payload: FileMutationInput) => void;
  onUpdate: (fileId: number, payload: FileMutationInput) => void;
  onRemove: (fileId: number) => Promise<void>;
}

const initials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const EventoFilesSection = ({
  title,
  itemLabel,
  icon: Icon,
  files,
  isSaving,
  onAdd,
  onUpdate,
  onRemove
}: Props) => {
  const [modalState, setModalState] = useState<{
    open: boolean;
    file: EventFileModel | null;
    onlyView: boolean;
  }>({ open: false, file: null, onlyView: false });
  const [deleteModalState, setDeleteModalState] = useState<{
    open: boolean;
    fileId: number;
  }>({ open: false, fileId: 0 });

  return (
    <div className="rounded-2xl border border-white bg-whiteLight p-6 dark:border-white/10 dark:bg-darkLight">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-secondary-500" />
          <h2 className="text-lg font-semibold text-ink dark:text-ink-dark">
            {title}
          </h2>
          <span className="rounded-full bg-secondary-50 px-2.5 py-0.5 text-xs font-medium text-secondary-700 dark:bg-white/10 dark:text-secondary-200">
            {files.length}
          </span>
        </div>
        <PlusButton size={24} onClick={() => setModalState({ open: true, file: null, onlyView: false })} />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {files.length === 0 && (
          <p className="col-span-full text-sm text-inkMuted dark:text-inkMuted-dark">
            Todavía no hay archivos cargados.
          </p>
        )}
        {files.map((file) => (
          <div
            key={file.id}
            className="group relative flex flex-col gap-2 rounded-xl border border-secondary-50 p-3 dark:border-white/5"
          >
            {file.user && (
              <span
                title={file.user.name}
                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-secondary-100 text-[10px] font-semibold text-secondary-700 shadow dark:border-surfaceDark dark:bg-white/10 dark:text-secondary-200"
              >
                {initials(file.user.name)}
              </span>
            )}
            <a
              href={file.url ?? undefined}
              target="_blank"
              rel="noreferrer"
              className="flex flex-col items-center gap-2 text-center"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50 text-red-500 dark:bg-red-500/10">
                <FileText size={18} />
              </span>
              <span className="line-clamp-2 text-xs font-medium text-ink dark:text-ink-dark">
                {file.name}
              </span>
            </a>
            <div className="absolute inset-x-0 top-20 flex items-center justify-center gap-3 lg:opacity-0 transition group-hover:opacity-100">
              <EyeButton
                onClick={() => setModalState({ open: true, file, onlyView: true })}
              />
              <TrashButton
                onClick={() =>
                  setDeleteModalState({ open: true, fileId: file.id })
                }
              />
            </div>
          </div>
        ))}
      </div>

      <FileUploadFormModal
        open={modalState.open}
        title={modalState.onlyView ? `Ver ${itemLabel}` : modalState.file ? `Editar ${itemLabel}` : `Nueva ${itemLabel}`}
        file={modalState.file}
        onlyView={modalState.onlyView}
        onEdit={() => setModalState({ open: true, file: modalState.file, onlyView: false })}
        isSaving={isSaving}
        onClose={() => setModalState({ open: false, file: null, onlyView: false })}
        onSave={(payload) => {
          if (modalState.file) {
            onUpdate(modalState.file.id, payload);
          } else {
            onAdd(payload);
          }
          setModalState({ open: false, file: null, onlyView: false });
        }}
      />
      <DeleteModal
        open={deleteModalState.open}
        onClose={() => setDeleteModalState({ open: false, fileId: 0 })}
        title="¿Eliminar este archivo?"
        body="Esta acción elimina el archivo de forma permanente."
        onRemove={async () => {
          try {
            await onRemove(deleteModalState.fileId);
            setDeleteModalState({ open: false, fileId: 0 });
          } catch (_) {
            // El aviso general muestra el error y permite reintentar.
          }
        }}
      />
    </div>
  );
};

export default EventoFilesSection;
